import { watch } from 'vue'

import { defineNuxtPlugin } from 'nuxt/app'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { AuthStatus } from '@common/auth-layer/store/auth.store'
import { getConfig } from '@common/core-layer/config'
import { getAuthCookie } from '@common/core-layer/utils/cookie.utils'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { preLoginRegionHeader, toRegionalUrl } from '@common/core-layer/utils/regional-host'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { useGlobal } from '../composables/useGlobal'
import { useSockets } from '../composables/useSockets'

export default defineNuxtPlugin(() => {
  if (!process.client) {
    return
  }

  const { connect, disconnect } = useSockets()
  const { store: authStore } = useAuth()
  const { store: currentUserStore } = useCurrentUser()
  const { locationId, getDefaultGokollabLocationData } = useLocation()
  const { isGokollab } = useGlobal()

  const resolveAuthToken = () => {
    return authStore.token || getAuthCookie()?.token || null
  }

  const resolveLocationId = () => {
    if (locationId.value) {
      return locationId.value
    }

    if (isGokollab) {
      return getDefaultGokollabLocationData().locationId
    }

    return null
  }

  const resolveGokollabContactId = () => {
    const profile = currentUserStore.profile
    if (profile && 'contactId' in profile) {
      return profile.contactId
    }
    return undefined
  }

  const connectIfReady = () => {
    if (authStore.status !== AuthStatus.AUTHENTICATED) {
      return
    }

    const token = resolveAuthToken()
    const contactId = authStore.contactId
    const locationIdValue = resolveLocationId()
    const config = getConfig()

    if (!token || !contactId || !locationIdValue || !config.COMMUNITIES_SOCKETS_SERVER_URL) {
      return
    }

    // Browser WS cannot send custom headers; AU tokens use the regional services host.
    const serverUrl = toRegionalUrl(
      config.COMMUNITIES_SOCKETS_SERVER_URL,
      preLoginRegionHeader(config.COMMUNITIES_SOCKETS_SERVER_URL, token)
    )

    connect({
      serverUrl,
      authToken: token,
      isGokollab,
      userV2: !isGokollab,
      gokollabContactId: resolveGokollabContactId() || contactId || undefined,
      path: '/sockets-communities/socket.io',
      channel: 'APP',
      source: 'PORTAL_USER',
      version: '2021-07-28',
      visitorId: '123',
    })
  }

  watch(
    [() => authStore.status, () => authStore.token, () => authStore.contactId, () => locationId.value, () => isGokollab],
    connectIfReady,
    { immediate: true }
  )

  watch(
    () => authStore.status,
    status => {
      if (status !== AuthStatus.AUTHENTICATED) {
        disconnect()
      }
    }
  )
})
